/**
 * BOGO promo — pack selector driver.
 *
 * Powers <bogo-pack-selector>: two pills ("1 book" / "2 books") that write straight
 * into the product form's quantity input, so a BOGO shopper reaches qty 2 in one tap
 * instead of having to discover the stepper.
 *
 * Keeps three things in sync:
 *   - pill click        -> quantity input value (+ input/change events for the form)
 *   - stepper/manual qty -> which pill reads as selected (neither, when qty > 2)
 *   - variants:change    -> the displayed unit and pack prices
 *
 * Everything is wrapped so a failure here can never block add-to-cart: on any error
 * we leave the server-rendered markup and the untouched quantity input in place.
 */
(function () {
  function formatMoney(cents) {
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
        var fmt = (window.theme && window.theme.money_format) || '';
        return window.Shopify.formatMoney(Math.round(cents), fmt);
      }
    } catch (e) {}
    return '$' + (Math.round(cents) / 100).toFixed(2);
  }

  class BogoPackSelector extends HTMLElement {
    connectedCallback() {
      try {
        this.secondPercent = parseFloat(this.getAttribute('second-percent'));
        if (isNaN(this.secondPercent)) this.secondPercent = 50;

        this.pills = Array.from(this.querySelectorAll('[data-pack]'));
        if (!this.pills.length) return;

        this.qtyInput = this._findQtyInput();
        this.unitPrice = parseInt(this.getAttribute('unit-price'), 10);

        this._onPillClick = (e) => {
          var pill = e.currentTarget;
          var qty = parseInt(pill.getAttribute('data-pack'), 10);
          if (!qty) return;
          this._setQty(qty);
          this._syncPills(qty);
        };
        this.pills.forEach((p) => p.addEventListener('click', this._onPillClick));

        // Manual stepper edits should deselect a pill rather than leave a stale one lit.
        if (this.qtyInput) {
          this._onQtyChange = () => this._syncPills(parseInt(this.qtyInput.value, 10));
          this.qtyInput.addEventListener('change', this._onQtyChange);
          this.qtyInput.addEventListener('input', this._onQtyChange);
          this._syncPills(parseInt(this.qtyInput.value, 10));
        }

        this._bindVariantChange();
      } catch (e) {}
    }

    disconnectedCallback() {
      try {
        if (this._onPillClick) {
          this.pills.forEach((p) => p.removeEventListener('click', this._onPillClick));
        }
        if (this.qtyInput && this._onQtyChange) {
          this.qtyInput.removeEventListener('change', this._onQtyChange);
          this.qtyInput.removeEventListener('input', this._onQtyChange);
        }
        if (this._variantEl && this._onVariantChange) {
          this._variantEl.removeEventListener('variants:change', this._onVariantChange);
        }
      } catch (e) {}
    }

    // The form id is authoritative; fall back to the nearest form for safety.
    _findQtyInput() {
      try {
        var formId = this.getAttribute('form-id');
        if (formId) {
          var byId = document.getElementById(formId + '--quantity');
          if (byId) return byId;
        }
        var scope = this.closest('.product-single__box, .product-single, form') || document;
        return scope.querySelector('input[name="quantity"]');
      } catch (e) {
        return null;
      }
    }

    _setQty(qty) {
      try {
        if (!this.qtyInput) return;
        this.qtyInput.value = qty;
        // quantity-input and the product form listen on these; dispatch both so the
        // stepper's own state and any price-per-qty display stay correct.
        this.qtyInput.dispatchEvent(new Event('input', { bubbles: true }));
        this.qtyInput.dispatchEvent(new Event('change', { bubbles: true }));
      } catch (e) {}
    }

    _syncPills(qty) {
      try {
        this.pills.forEach((p) => {
          var match = parseInt(p.getAttribute('data-pack'), 10) === qty;
          p.classList.toggle('is-selected', match);
          p.setAttribute('aria-pressed', match ? 'true' : 'false');
        });
      } catch (e) {}
    }

    // variants:change does not bubble, so listen on the selector element itself.
    _bindVariantChange() {
      try {
        var scope = this.closest('.product-single__box, .product-single') || document;
        this._variantEl = scope.querySelector(
          'variant-selects, variant-radios, variant-swatches, variant-mixed-inputs'
        );
        if (!this._variantEl) return; // single-variant product: server prices stand
        this._onVariantChange = (e) => {
          try {
            var v = e && e.detail && e.detail.variant;
            if (v && typeof v.price === 'number') this._renderPrices(v.price);
          } catch (err) {}
        };
        this._variantEl.addEventListener('variants:change', this._onVariantChange);
      } catch (e) {}
    }

    _renderPrices(unitPrice) {
      try {
        this.unitPrice = unitPrice;
        var discount = Math.round((unitPrice * this.secondPercent) / 100);
        var packPrice = unitPrice * 2 - discount;
        var packFull = unitPrice * 2;
        var savePct = packFull > 0 ? Math.round((discount * 100) / packFull) : 0;

        var set = (sel, text) => {
          var el = this.querySelector(sel);
          if (el) el.textContent = text;
        };
        set('[data-pack-single-price]', formatMoney(unitPrice));
        set('[data-pack-double-price]', formatMoney(packPrice));
        set('[data-pack-double-was]', formatMoney(packFull));
        set('[data-pack-save]', savePct + '%');
      } catch (e) {}
    }
  }

  try {
    if (window.customElements && !customElements.get('bogo-pack-selector')) {
      customElements.define('bogo-pack-selector', BogoPackSelector);
    }
  } catch (e) {}
})();
